// Marca de agua «BORRADOR» (UNA sola, DIAGONAL, hoja completa) para los documentos
// que aún no completan su cadena de firmas (acta de reparto sin FIRMAR, decisiones
// sin APROBAR). Cuando todos firman, el documento se genera limpio (sin marca).
//
// ⚠️ Cada camino usa UNA sola técnica para no duplicar la marca (docx-preview SÍ
// alcanza a dibujar el cuadro VML, así que si el blob de la preview lo trajera,
// se verían dos):
//  - `marcarBorradorZip(zip)` inyecta la marca en los headers del .docx — SOLO
//    para el .docx que se DESCARGA (Word la repite en todas las hojas).
//  - `superponerBorrador(raiz)` pinta la marca como capa CSS sobre cada página de
//    docx-preview — SOLO para la previsualización y la ventana de imprimir→PDF
//    (cuyo blob va SIN la marca VML).

// Cuadro de texto VML DIAGONAL (rotation 315 = -45°), centrado en la PÁGINA,
// detrás del texto (z-index negativo). Los xmlns van inline para no depender de
// los que declare cada header. sz 144 = 72 pt.
const PARRAFO_MARCA =
  '<w:p><w:pPr><w:rPr><w:noProof/></w:rPr></w:pPr><w:r><w:rPr><w:noProof/></w:rPr><w:pict>'
  + '<v:shape xmlns:v="urn:schemas-microsoft-com:vml" xmlns:o="urn:schemas-microsoft-com:office:office" '
  + 'id="MarcaBorrador" o:allowincell="f" filled="f" stroked="f" '
  + 'style="position:absolute;margin-left:0;margin-top:0;width:560pt;height:150pt;rotation:315;z-index:-251654144;'
  + 'mso-position-horizontal:center;mso-position-horizontal-relative:page;'
  + 'mso-position-vertical:center;mso-position-vertical-relative:page">'
  + '<v:textbox inset="0,0,0,0">'
  + '<w:txbxContent><w:p><w:pPr><w:jc w:val="center"/></w:pPr>'
  + '<w:r><w:rPr><w:rFonts w:ascii="Arial" w:hAnsi="Arial"/><w:color w:val="D9D9D9"/>'
  + '<w:spacing w:val="80"/><w:sz w:val="144"/><w:szCs w:val="144"/><w:b/></w:rPr>'
  + '<w:t>BORRADOR</w:t></w:r></w:p></w:txbxContent>'
  + '</v:textbox></v:shape>'
  + '</w:pict></w:r></w:p>';

/** Zip de docxtemplater/pizzip (solo lo que usamos). */
interface ZipDocx {
  files: Record<string, unknown>;
  file(nombre: string): { asText(): string } | null;
  file(nombre: string, contenido: string): unknown;
}

/**
 * Inyecta la marca BORRADOR en cada header del .docx renderizado (Word la repite
 * en todas las páginas). Usar SOLO para el .docx que se descarga (la preview y
 * el PDF llevan la marca por CSS). Idempotente: no la duplica si ya está.
 */
export function marcarBorradorZip(zip: ZipDocx): void {
  for (const nombre of Object.keys(zip.files)) {
    if (!/^word\/header\d*\.xml$/.test(nombre)) continue;
    const archivo = zip.file(nombre);
    if (!archivo) continue;
    const xml = archivo.asText();
    if (xml.includes('MarcaBorrador')) continue;
    zip.file(nombre, xml.replace(/(<w:hdr[^>]*>)/, `$1${PARRAFO_MARCA}`));
  }
}

/**
 * Superpone UNA marca «BORRADOR» (diagonal, hoja completa) sobre cada página
 * renderizada por docx-preview dentro de `raiz`. Llamar DESPUÉS de renderAsync,
 * con un blob SIN la marca VML (para no verla doble).
 */
export function superponerBorrador(raiz: HTMLElement): void {
  raiz.querySelectorAll<HTMLElement>('section.docx').forEach((pagina) => {
    if (pagina.querySelector(':scope > .marca-borrador')) return;
    if (!pagina.style.position) pagina.style.position = 'relative';
    pagina.style.overflow = 'hidden';
    const capa = document.createElement('div');
    capa.className = 'marca-borrador';
    capa.style.cssText =
      'position:absolute;inset:0;display:flex;align-items:center;justify-content:center;'
      + 'pointer-events:none;z-index:1;';
    const texto = document.createElement('span');
    texto.textContent = 'BORRADOR';
    const alto = pagina.clientHeight || 1100;
    texto.style.cssText =
      'transform:rotate(-45deg);white-space:nowrap;font-family:Arial,Helvetica,sans-serif;'
      + `font-weight:700;letter-spacing:0.22em;color:rgba(148,163,184,0.28);font-size:${Math.round(alto / 7.5)}px;`;
    capa.appendChild(texto);
    pagina.appendChild(capa);
  });
}
